import { supabase } from './supabase';
import { databaseService } from './databaseService';
import { UserServiceLogger } from '../utils/logger';
import { APP_CONSTANTS } from '../config/constants';
import { globalState, userActions, UserProfile, companyActions, CompanyProfile } from '../state/globalState';
import { uploadImageHelper, deleteImageHelper } from '../utils/file/uploadImageHelper';
import { debugPause } from '@/utils/debugPause';
//import { debugPause } from '../utils/debugPause';

/**
 * Interface para os dados do perfil do usuário
 */
export interface UserProfileData {
    id: string;
    name?: string;
    email?: string;
    telefone?: string;
    photo_url?: string;
    empresa_id?: string;
    user_type?: string;
}

/**
 * Obtém os dados do perfil do usuário do banco local ou remoto
 * @param userId ID do usuário
 * @param options Guarda de publicação e controle facultativo do cache remoto, ativo por padrão.
 * @returns Dados do perfil do usuário
 */
export const getUserProfile = async (
  userId: string,
  options: { shouldApply?: () => boolean; cacheRemote?: boolean } = {},
): Promise<{ data: UserProfileData | null, error: any }> => {
    UserServiceLogger.GetUserProfile.debug(`Obtendo perfil do usuário: ${userId}`);

    if (!userId) {
        UserServiceLogger.GetUserProfile.warn('Tentativa de obter perfil sem ID de usuário');
        return { data: null, error: new Error('ID de usuário não fornecido') };
    }

    try {
        // Buscar no banco local primeiro
        UserServiceLogger.GetUserProfile.debug(`Buscando perfil no banco local para: ${userId}`);
        const localUser = await databaseService.readItem<UserProfileData>(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME, userId);
        if (options.shouldApply?.() === false) return { data: null, error: null };

        if (localUser) {
            UserServiceLogger.GetUserProfile.info(`Perfil encontrado no banco local: ${userId}`);

            // Atualizar o estado global com dados locais primeiro para interface responder rapidamente
            userActions.setProfile({
                ...localUser
            } as UserProfile);

            return {
                data: {
                    id: localUser.id,
                    name: localUser.name,
                    email: localUser.email,
                    telefone: localUser.telefone,
                    photo_url: localUser.photo_url,
                    empresa_id: localUser.empresa_id,
                    user_type: localUser.user_type
                },
                error: null
            };
        }

        // Se estiver offline, retornar os dados locais sem tentar buscar do Supabase
        if (!globalState.network.isConnected.get()) {
            UserServiceLogger.GetUserProfile.info(`Modo offline: usando dados locais para o usuário ${userId}`);
            return { data: localUser || null, error: null };
        }

        // Se não encontrar local, buscar no Supabase
        UserServiceLogger.GetUserProfile.debug(`Perfil não encontrado localmente. Verificando no Supabase...`);
        const { data: remoteUser, error } = await supabase
            .from(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME)
            .select('*')
            .eq('id', userId)
            .single();
        if (options.shouldApply?.() === false) return { data: null, error: null };

        if (error) {
            UserServiceLogger.GetUserProfile.error('Erro ao buscar perfil no Supabase:', error);
            return { data: null, error };
        }

        if (remoteUser) {
            UserServiceLogger.GetUserProfile.info(`Perfil encontrado no Supabase: ${userId}`);

            // Leituras temporárias podem publicar o perfil sem iniciar cache remoto.
            if (options.cacheRemote !== false) {
                try {
                    await databaseService.createItem(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME, remoteUser, {
                        skipPendingOperation: true,
                        source: 'remote',
                    });
                    UserServiceLogger.GetUserProfile.debug(`Perfil salvo no banco local com sucesso`);
                } catch (saveError) {
                    UserServiceLogger.GetUserProfile.warn('Erro ao salvar perfil no banco local:', saveError);
                }
            }
            if (options.shouldApply?.() === false) return { data: null, error: null };

            // Atualizar o estado global com dados do servidor
            userActions.setProfile({
                ...remoteUser
            } as UserProfile);

            return {
                data: {
                    id: remoteUser.id,
                    name: remoteUser.name,
                    email: remoteUser.email,
                    telefone: remoteUser.telefone,
                    photo_url: remoteUser.photo_url,
                    empresa_id: remoteUser.empresa_id,
                    user_type: remoteUser.user_type
                },
                error: null
            };
        }

        UserServiceLogger.GetUserProfile.warn(`Perfil não encontrado para o usuário: ${userId}`);
        return { data: null, error: new Error('Perfil não encontrado') };
    } catch (error) {
        UserServiceLogger.GetUserProfile.error('Exceção ao obter perfil do usuário:', error);
        return { data: null, error };
    }
};

/**
 * Obtém os dados da empresa do usuário
 * @param empresaId ID da empresa
 * @returns Dados da empresa
 */
export const getUserCompany = async (empresaId: string) => {
    // Limpa erros anteriores e define loading
    companyActions.setCompanyError(null);
    companyActions.setCompanyLoading(true);

    UserServiceLogger.GetUserCompany.debug(`Obtendo dados da empresa: ${empresaId}`);

    if (!empresaId) {
        UserServiceLogger.GetUserCompany.warn('Tentativa de obter empresa sem ID');
        const error = new Error('ID de empresa não fornecido');
        companyActions.setCompanyError(error.message);
        companyActions.setCompanyLoading(false);
        return { data: null, error };
    }

    try {
        // Buscar no banco local primeiro
        const localEmpresa = await databaseService.readItem<CompanyProfile>(APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME, empresaId);

        if (localEmpresa) {
            UserServiceLogger.GetUserCompany.info(`Empresa encontrada no banco local: ${empresaId}`);

            // Atualizar o estado global da empresa
            companyActions.setCompanyData(localEmpresa);
            companyActions.setCompanyLoading(false);
            return { data: localEmpresa, error: null };
        }

        // Se não encontrar local, buscar no Supabase
        const { data: remoteEmpresa, error } = await supabase
            .from(APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME)
            .select('*')
            .eq('id', empresaId)
            .single();

        if (error) {
            UserServiceLogger.GetUserCompany.error('Erro ao buscar empresa no Supabase:', error);
            companyActions.setCompanyError(error.message);
            companyActions.setCompanyLoading(false);
            return { data: null, error };
        }

        if (remoteEmpresa) {
            UserServiceLogger.GetUserCompany.info(`Empresa encontrada no Supabase: ${empresaId}`);

            // Salvar no banco local
            try {
                await databaseService.createItem(APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME, remoteEmpresa, {
                    skipPendingOperation: true,
                    source: 'remote',
                });
                UserServiceLogger.GetUserCompany.debug(`Empresa salva no banco local com sucesso`);
            } catch (saveError) {
                UserServiceLogger.GetUserCompany.warn('Erro ao salvar empresa no banco local:', saveError);
            }

            // Atualizar o estado global da empresa
            companyActions.setCompanyData(remoteEmpresa as CompanyProfile);
            companyActions.setCompanyLoading(false);
            return { data: remoteEmpresa, error: null };
        }

        UserServiceLogger.GetUserCompany.warn(`Empresa não encontrada: ${empresaId}`);
        const notFoundError = new Error('Empresa não encontrada');
        companyActions.setCompanyError(notFoundError.message);
        companyActions.setCompanyLoading(false);
        return { data: null, error: notFoundError };
    } catch (error) {
        UserServiceLogger.GetUserCompany.error('Exceção ao obter dados da empresa:', error);
        if (error instanceof Error) {
            companyActions.setCompanyError(error.message);
        } else {
            companyActions.setCompanyError('Erro desconhecido ao obter dados da empresa');
        }
        companyActions.setCompanyLoading(false);
        return { data: null, error };
    }
};

/**
 * Atualiza os dados do perfil do usuário
 * @param userId ID do usuário
 * @param profileData Dados atualizados do perfil
 * @returns Resultado da atualização
 */
export const updateUserProfile = async (userId: string, profileData: Partial<UserProfileData>) => {
    UserServiceLogger.UpdateUserProfile.debug(`Atualizando perfil do usuário: ${userId}`);

    if (!userId) {
        UserServiceLogger.UpdateUserProfile.warn('Tentativa de atualizar perfil sem ID de usuário');
        return { success: false, error: new Error('ID de usuário não fornecido') };
    }

    // Remover ID e email dos dados a atualizar, se presentes
    const updateData = { ...profileData };
    delete updateData.id;
    delete updateData.email; // Email não deve ser atualizado desta forma

    try {
        // Primeiro verificar se o usuário existe
        const { data: existingUser, error: checkError } = await getUserProfile(userId);

        if (checkError || !existingUser) {
            UserServiceLogger.UpdateUserProfile.error('Erro ao verificar existência do usuário:', checkError);
            return { success: false, error: checkError || new Error('Usuário não encontrado') };
        }

        // Atualizar no Supabase
        UserServiceLogger.UpdateUserProfile.debug(`Atualizando perfil no Supabase: ${userId}`);
        const { data, error } = await supabase
            .from(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME)
            .update(updateData)
            .eq('id', userId)
            .select()
            .single();

        if (error) {
            UserServiceLogger.UpdateUserProfile.error('Erro ao atualizar perfil no Supabase:', error);
            return { success: false, error };
        }

        // Atualizar no banco local
        try {
            await databaseService.updateItem(
                APP_CONSTANTS.DATABASE.TABLES.USERS.NAME,
                userId,
                (data as Partial<UserProfileData>) ?? updateData,
                { skipPendingOperation: true, source: 'remote' }
            );
            UserServiceLogger.UpdateUserProfile.debug(`Perfil atualizado no banco local após confirmação remota`);
        } catch (updateError) {
            UserServiceLogger.UpdateUserProfile.warn('Erro ao atualizar perfil no banco local após confirmação remota:', updateError);
            // Continuar mesmo com erro no banco local
        }

        // Atualizar o estado global
        if (data) {
            // Verificar se o retorno do Supabase contém todos os campos necessários
            const updatedData = data || {};

            // Obter o perfil atual para não perder campos
            const currentProfile = globalState.user.profile.get();

            // Filtrar valores undefined para evitar sobrescrever com undefined
            const safeUpdate = Object.fromEntries(
                Object.entries(updatedData).filter(([_, v]) => v !== undefined)
            );

            // Fazer um merge seguro, mantendo os campos que não foram atualizados
            userActions.setProfile({
                ...currentProfile,
                ...safeUpdate
            } as UserProfile);
        }

        UserServiceLogger.UpdateUserProfile.info(`Perfil atualizado com sucesso: ${userId}`);
        return { success: true, data };
    } catch (error) {
        UserServiceLogger.UpdateUserProfile.error('Exceção ao atualizar perfil do usuário:', error);
        return { success: false, error };
    }
};

/**
 * Atualiza os dados da empresa do usuário
 * @param companyId ID da empresa
 * @param companyData Dados parciais da empresa
 * @returns Resultado da atualização
 */
export const updateCompanyProfile = async (companyId: string, companyData: Partial<CompanyProfile>) => {
    UserServiceLogger.UpdateCompanyProfile.debug(`Atualizando dados da empresa: ${companyId}`);

    if (!companyId) {
        UserServiceLogger.UpdateCompanyProfile.warn('Tentativa de atualizar empresa sem ID');
        return { success: false, error: new Error('ID da empresa não fornecido') };
    }

    const updateData = { ...companyData } as Partial<CompanyProfile> & { id?: string };
    delete updateData.id;

    try {
        // Verificar se a empresa existe no Supabase
        const { data: existingCompany, error: checkError } = await supabase
            .from(APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME)
            .select('*')
            .eq('id', companyId)
            .single();

        if (checkError || !existingCompany) {
            UserServiceLogger.UpdateCompanyProfile.error('Erro ao verificar existência da empresa:', checkError);
            return { success: false, error: checkError || new Error('Empresa não encontrada') };
        }

        // Atualizar no Supabase
        const { data, error } = await supabase
            .from(APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME)
            .update(updateData)
            .eq('id', companyId)
            .select('*')
            .single();

        if (error) {
            UserServiceLogger.UpdateCompanyProfile.error('Erro ao atualizar empresa no Supabase:', error);
            return { success: false, error };
        }

        // Atualizar no banco local sem criar nova pendência (a operação já foi confirmada no remoto)
        try {
            await databaseService.updateItem(
                APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME,
                companyId,
                (data as Partial<CompanyProfile>) ?? updateData,
                { skipPendingOperation: true, source: 'remote' }
            );
            UserServiceLogger.UpdateCompanyProfile.debug('Empresa atualizada no banco local após confirmação remota');
        } catch (updateError) {
            UserServiceLogger.UpdateCompanyProfile.warn('Erro ao atualizar empresa no banco local após confirmação remota:', updateError);
        }

        // Atualizar estado global com merge seguro
        const safeUpdate = data
            ? Object.fromEntries(Object.entries(data).filter(([_, v]) => v !== undefined))
            : updateData;

        const currentCompany = globalState.company.data.get();
        if (currentCompany) {
            companyActions.setCompanyData({
                ...currentCompany,
                ...(safeUpdate as Partial<CompanyProfile>),
            } as CompanyProfile);
        } else if (data) {
            companyActions.setCompanyData(data as CompanyProfile);
        }

        UserServiceLogger.UpdateCompanyProfile.info(`Empresa atualizada com sucesso: ${companyId}`);
        return { success: true, data };
    } catch (error) {
        UserServiceLogger.UpdateCompanyProfile.error('Exceção ao atualizar dados da empresa:', error);
        return { success: false, error };
    }
};

/**
 * Faz o upload da imagem de perfil do usuário
 * Utiliza a estratégia local-first com fallback
 * @param userId ID do usuário
 * @param localUri URI local da imagem
 * @returns Resultado da operação
 */
export const uploadUserProfileImage = async (userId: string, localUri: string) => {  
  try {
    UserServiceLogger.UploadUserProfileImage.debug(`Iniciando upload de imagem para o usuário: ${userId}`);
    
    if (!userId) {
      UserServiceLogger.UploadUserProfileImage.info('ID de usuário não fornecido');
      throw new Error('ID de usuário não fornecido');
    }
    
    if (!localUri) {
      UserServiceLogger.UploadUserProfileImage.info('URI da imagem não fornecido');
      throw new Error('URI da imagem não fornecido');
    }
    
    UserServiceLogger.UploadUserProfileImage.info(`passou checks iniciais`);
    // Obter o perfil atual para verificar imagens anteriores
    const currentProfile = globalState.user.profile.get();
    const previousLocalUri = currentProfile?.photo_local_path || null;
    const previousImageUrl = currentProfile?.photo_url || null;
    
    UserServiceLogger.UploadUserProfileImage.info(`Atualizando o estado global do usuário com o caminho local: ${localUri}`);
    UserServiceLogger.UploadUserProfileImage.debug(`Imagem anterior local: ${previousLocalUri || 'nenhuma'}`);
    UserServiceLogger.UploadUserProfileImage.debug(`Imagem anterior remota: ${previousImageUrl || 'nenhuma'}`);
    
    //await debugPause('check 1');
    // Atualizar o estado global do usuário com o caminho local (para feedback visual imediato)
    if (currentProfile) {
      userActions.setProfile({
        ...currentProfile,
        photo_local_path: localUri
      });
    }
    
    UserServiceLogger.UploadUserProfileImage.info(`Chamando uploadImageHelper com localUri: ${localUri}`);
    // Utilizar o helper de upload de imagens com parâmetros de imagens anteriores
    const result = await uploadImageHelper({
      localUri,
      itemType: 'USER',
      itemId: userId,
      columnName: 'photo_url',
      previousLocalUri,
      previousImageUrl
    });
    
    UserServiceLogger.UploadUserProfileImage.info(`Upload concluído. URL pública: ${result.publicURL}, Path: ${result.storagePath}`);
    
    // Após o upload, recarregar os dados do perfil para atualizar o estado global
    await getUserProfile(userId);
    
    return { success: true, data: result };
  } catch (error) {
    UserServiceLogger.UpdateUserProfile.error('Erro ao fazer upload da imagem de perfil:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro desconhecido ao fazer upload da imagem'
    };
  }
};

/**
 * Faz o upload da logo da empresa
 * Utiliza a estratégia local-first com fallback
 * @param companyId ID da empresa
 * @param localUri URI local da imagem
 * @returns Resultado da operação
 */
export const uploadCompanyLogo = async (companyId: string, localUri: string) => {  
  try {
    UserServiceLogger.UploadUserProfileImage.debug(`Iniciando upload do logo para a empresa: ${companyId}`);
    
    if (!companyId) {
      UserServiceLogger.UploadUserProfileImage.info('ID de empresa não fornecido');
      throw new Error('ID de empresa não fornecido');
    }
    
    if (!localUri) {
      UserServiceLogger.UploadUserProfileImage.info('URI da imagem não fornecido');
      throw new Error('URI da imagem não fornecido');
    }
    
    // Obter o perfil atual da empresa para verificar imagens anteriores
    const currentCompany = globalState.company.data.get();
    const previousLocalUri = currentCompany?.logo_local_path || null;
    const previousImageUrl = currentCompany?.logo_url || null;
    
    UserServiceLogger.UploadUserProfileImage.info(`Atualizando o estado global da empresa com o caminho local: ${localUri}`);
    UserServiceLogger.UploadUserProfileImage.info(`Imagem anterior local: ${previousLocalUri || 'nenhuma'}`);
    UserServiceLogger.UploadUserProfileImage.info(`Imagem anterior remota: ${previousImageUrl || 'nenhuma'}`);
    
    // Atualizar o estado global da empresa com o caminho local (para feedback visual imediato)
    if (currentCompany) {
      companyActions.setCompanyData({
        ...currentCompany,
        logo_local_path: localUri
      });
    }
    
    UserServiceLogger.UploadUserProfileImage.info(`Chamando uploadImageHelper com localUri: ${localUri}`);
    // Utilizar o helper de upload de imagens com parâmetros de imagens anteriores
    const result = await uploadImageHelper({
      localUri,
      itemType: 'COMPANY',
      itemId: companyId,
      columnName: 'logo_url',
      previousLocalUri,
      previousImageUrl
    });
    
    UserServiceLogger.UploadUserProfileImage.info(`Upload concluído. URL pública: ${result.publicURL}, Path: ${result.storagePath}`);
    
    // Após o upload, recarregar os dados da empresa para atualizar o estado global
    await getUserCompany(companyId);
    
    return { success: true, data: result };
  } catch (error) {
    UserServiceLogger.UpdateUserProfile.error('Erro ao fazer upload do logo da empresa:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro desconhecido ao fazer upload da imagem'
    };
  }
};

/**
 * Faz o upload da imagem de perfil de um usuário da empresa sem interferir no estado global do usuário logado
 * Utiliza a estratégia local-first com fallback
 * @param userId ID do usuário da empresa
 * @param localUri URI local da imagem
 * @returns Resultado da operação
 */
export const uploadCompanyUserProfileImage = async (userId: string, localUri: string) => {  
  try {
    UserServiceLogger.UploadUserProfileImage.debug(`Iniciando upload de imagem para o usuário da empresa: ${userId}`);
    
    if (!userId) {
      UserServiceLogger.UploadUserProfileImage.info('ID de usuário não fornecido');
      throw new Error('ID de usuário não fornecido');
    }
    
    if (!localUri) {
      UserServiceLogger.UploadUserProfileImage.info('URI da imagem não fornecido');
      throw new Error('URI da imagem não fornecido');
    }
    
    UserServiceLogger.UploadUserProfileImage.info(`Verificando dados do usuário no banco de dados local`);
    
    // Em vez de usar o estado global, buscar os dados do usuário específico diretamente do DB
    const userData = await databaseService.readItem<{
      id: string;
      name: string;
      email: string;
      telefone?: string;
      photo_url?: string;
      photo_local_path?: string;
      user_type: string;
      created_at: string;
    }>(
      APP_CONSTANTS.DATABASE.TABLES.USERS.NAME, 
      userId
    );
    
    if (!userData) {
      UserServiceLogger.UploadUserProfileImage.error(`Usuário não encontrado: ${userId}`);
      throw new Error('Usuário não encontrado');
    }
    
    // Obter caminhos de imagens anteriores diretamente dos dados do usuário
    const previousLocalUri = userData.photo_local_path || null;
    const previousImageUrl = userData.photo_url || null;
    
    UserServiceLogger.UploadUserProfileImage.debug(`Imagem anterior local: ${previousLocalUri || 'nenhuma'}`);
    UserServiceLogger.UploadUserProfileImage.debug(`Imagem anterior remota: ${previousImageUrl || 'nenhuma'}`);
    
    // Atualizar APENAS o banco de dados local com o caminho local (sem afetar o estado global)
    await databaseService.updateItem(
      APP_CONSTANTS.DATABASE.TABLES.USERS.NAME, 
      userId, 
      { photo_local_path: localUri },
      true // true para skip pending operations
    );
    
    UserServiceLogger.UploadUserProfileImage.info(`Chamando uploadImageHelper com localUri: ${localUri}`);
    
    // Utilizar o helper de upload de imagens com parâmetros de imagens anteriores
    const result = await uploadImageHelper({
      localUri,
      itemType: 'USER',
      itemId: userId,
      columnName: 'photo_url',
      previousLocalUri,
      previousImageUrl
    });
    
    UserServiceLogger.UploadUserProfileImage.info(`Upload concluído. URL pública: ${result.publicURL}, Path: ${result.storagePath}`);    
    
    // Buscar e retornar os dados atualizados
    const updatedUser = await databaseService.readItem<{
      id: string;
      name: string;
      email: string;
      telefone?: string;
      photo_url?: string;
      photo_local_path?: string;
      user_type: string;
      created_at: string;
    }>(
      APP_CONSTANTS.DATABASE.TABLES.USERS.NAME, 
      userId
    );
    
    // Retornar o usuário atualizado
    return { 
      success: true, 
      data: {
        ...result,
        user: updatedUser
      }
    };
  } catch (error) {
    UserServiceLogger.UpdateUserProfile.error('Erro ao fazer upload da imagem de perfil do usuário da empresa:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro desconhecido ao fazer upload da imagem'
    };
  }
};

/**
 * Deleta a imagem de perfil do usuário (local e remota), seguindo o padrão offline-first.
 * Remove do Supabase Storage, do armazenamento local e atualiza o banco local.
 * Registra pendências em caso de falha, com logs detalhados.
 * @param userId ID do usuário
 * @returns {Promise<{ success: boolean; error?: string }>} Resultado da operação
 */
export const deleteUserProfileImage = async (userId: string) => {
  try {
    UserServiceLogger.Main.debug(`Iniciando deleção de imagem para o usuário: ${userId}`);
    if (!userId) {
      UserServiceLogger.Main.info('ID de usuário não fornecido');
      throw new Error('ID de usuário não fornecido');
    }
    // Obter o perfil atual para verificar imagens anteriores
    const currentProfile = globalState.user.profile.get();
    const previousLocalUri = currentProfile?.photo_local_path || null;
    const previousImageUrl = currentProfile?.photo_url || null;
    UserServiceLogger.Main.debug(`Imagem anterior local: ${previousLocalUri || 'nenhuma'}`);
    UserServiceLogger.Main.debug(`Imagem anterior remota: ${previousImageUrl || 'nenhuma'}`);
    // Chamar helper utilitário
    const result = await deleteImageHelper({
      itemType: 'USER',
      itemId: userId,
      columnName: 'photo_url',
      localPathColumn: 'photo_local_path',
      previousLocalUri,
      previousImageUrl
    });
    if (!result.success) {
      throw new Error(result.error);
    }
    // Após a deleção, recarregar os dados do perfil para atualizar o estado global
    await getUserProfile(userId);
    return { success: true };
  } catch (error) {
    UserServiceLogger.Main.error('Erro ao deletar a imagem de perfil:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro desconhecido ao deletar a imagem'
    };
  }
};

/**
 * Deleta o logo da empresa
 * Utiliza a estratégia local-first com fallback
 * @param companyId ID da empresa
 * @returns Resultado da operação
 */
export const deleteCompanyLogo = async (companyId: string) => {
  try {
    UserServiceLogger.UploadUserProfileImage.debug(`Iniciando deleção do logo para a empresa: ${companyId}`);
    
    if (!companyId) {
      UserServiceLogger.UploadUserProfileImage.info('ID de empresa não fornecido');
      throw new Error('ID de empresa não fornecido');
    }
    
    // Obter o perfil atual da empresa para verificar imagens anteriores
    const currentCompany = globalState.company.data.get();
    const previousLocalUri = currentCompany?.logo_local_path || null;
    const previousImageUrl = currentCompany?.logo_url || null;
    
    // Chamar o helper de deleção de imagens
    const result = await deleteImageHelper({
      itemType: 'COMPANY',
      itemId: companyId,
      columnName: 'logo_url',
      localPathColumn: 'logo_local_path',
      previousLocalUri,
      previousImageUrl
    });
    if (!result.success) {
      throw new Error(result.error);
    }
    
    // Após a deleção, recarregar os dados da empresa para atualizar o estado global
    await getUserCompany(companyId);
    
    return { success: true };
  } catch (error) {
    UserServiceLogger.UpdateUserProfile.error('Erro ao deletar o logo da empresa:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Erro desconhecido ao deletar a imagem'
    };
  }
};

export default {
    getUserProfile,
    getUserCompany,
    updateUserProfile,
    updateCompanyProfile,
    uploadUserProfileImage,
    uploadCompanyLogo,
    uploadCompanyUserProfileImage,
    deleteUserProfileImage
};
